/**
 * Home's visual language, kept apart from the page that uses it.
 *
 * Two things live here. The **accent per section**, because a workstation that
 * is about two modalities should say which one you are looking at without
 * making you read: diffusion is the blue the rest of the app already uses,
 * perfusion is warm — Tmax and TTP maps are read on warm scales, so the colour
 * is borrowed from the data rather than picked from a palette — and the three
 * cross-cutting sections take cool hues that do not compete with either.
 * Accents appear as a rule, a dot or a tint, never as a filled block: colour
 * here is a label, and a page that shouts has nothing left for the images.
 *
 * And the **motif**: nested isocontours, the shape a thresholded lesion makes
 * when you draw it at several levels at once. It is decoration, and it says so
 * — no axes, no legend, nothing a reader could mistake for a measurement. It
 * exists because a masthead of flat text on flat grey reads as a settings
 * page, and this is the front of an instrument.
 */

import { cn } from "@/lib/utils";

export interface Accent {
  /** The line, dot and tint colour. */
  base: string;
  /** Background wash behind an icon or a hovered row. */
  soft: string;
}

export const ACCENTS: Record<string, Accent> = {
  dwi: { base: "#2563eb", soft: "#eff6ff" },
  perfusion: { base: "#c2410c", soft: "#fff7ed" },
  quantification: { base: "#047857", soft: "#ecfdf5" },
  view: { base: "#6d28d9", soft: "#f5f3ff" },
  developer: { base: "#475569", soft: "#f1f5f9" },
};

export const NEUTRAL_ACCENT: Accent = { base: "#475569", soft: "#f1f5f9" };

export function accentFor(id: string): Accent {
  return ACCENTS[id] ?? NEUTRAL_ACCENT;
}

/** Accent as CSS custom properties, so children can use `var(--accent)`. */
export function accentVars(accent: Accent): React.CSSProperties {
  return { "--accent": accent.base, "--accent-soft": accent.soft } as React.CSSProperties;
}

/**
 * One irregular closed contour, drawn several times at falling scale. Using a
 * single path for every ring is what makes them look like level sets of one
 * field rather than a stack of unrelated blobs.
 */
const CONTOUR =
  "M100,30 C130,28 162,48 168,80 C174,112 156,148 124,162 C92,176 54,166 36,140 C18,114 24,72 48,48 C64,32 82,31 100,30 Z";

const RINGS = [
  { scale: 1.0, rotate: 0, opacity: 0.16 },
  { scale: 0.84, rotate: 5, opacity: 0.22 },
  { scale: 0.68, rotate: 10, opacity: 0.3 },
  { scale: 0.52, rotate: 15, opacity: 0.4 },
  { scale: 0.37, rotate: 20, opacity: 0.55 },
  { scale: 0.23, rotate: 25, opacity: 0.75 },
];

export function ContourMotif({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 200 200"
      aria-hidden
      className={cn("pointer-events-none select-none", className)}
      style={{
        // Fades into the surface instead of ending on a hard edge, so the
        // masthead reads as one field rather than as a card with a picture in
        // the corner.
        maskImage: "radial-gradient(closest-side, #000 55%, transparent 100%)",
        WebkitMaskImage: "radial-gradient(closest-side, #000 55%, transparent 100%)",
      }}
    >
      <g stroke="var(--color-ads-accent)" fill="none" strokeWidth="0.9">
        {RINGS.map((r) => (
          <path
            key={r.scale}
            d={CONTOUR}
            opacity={r.opacity}
            transform={`translate(100 100) rotate(${r.rotate}) scale(${r.scale}) translate(-100 -100)`}
          />
        ))}
      </g>
      <g stroke="var(--color-ads-accent)" opacity="0.18" strokeWidth="0.6">
        <line x1="100" y1="14" x2="100" y2="186" />
        <line x1="14" y1="100" x2="186" y2="100" />
      </g>
    </svg>
  );
}

/** A small caps label above a title. Sets the page's rhythm. */
export function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** The one card surface on this page: hairline, soft lift, no heavy shadow. */
export const SURFACE =
  "rounded-2xl border border-ads-border bg-ads-surface shadow-[0_1px_2px_rgba(16,24,40,0.03),0_1px_3px_rgba(16,24,40,0.05)]";
